'use client';

import { getPersistedPin, setPersistedPin } from '@modules/prism-client/lib/manifest/pin-storage';
import { Skeleton } from '@tuic/web-compositions/core/Skeleton';
import { InteractionHeader } from '@tuic/web-compositions/my-trips/InteractionHeader';
import { ManifestOverview, type ManifestOverviewProps } from '@tuic/web-compositions/my-trips/ManifestOverview';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useLayoutEffect } from 'react';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { FC } from 'react';
export type ManifestOverviewBlockProps = Omit<ManifestOverviewProps, 'i18n'> & {
  i18n?: NonNullable<ManifestOverviewProps['i18n']> & {
    headerTitle: string;
    headerBack: string;
  };
};
const ManifestOverviewHeader: FC<{
  title?: string;
  backLabel?: string;
}> = ({
  title,
  backLabel
}) => {
  const pathname = usePathname() ?? '';
  const backHref = '/' + pathname.split('/').filter(Boolean).slice(0, -1).join('/');
  return <div className="sticky top-0 z-10 mb-8 bg-section-gray md:mb-4 xl:mb-8" data-sentry-component="ManifestOverviewHeader" data-sentry-source-file="ManifestOverviewBlock.tsx">
      <InteractionHeader title={title ?? <Skeleton className="h-2 w-[150px]" />} leftButton={{
      iconName: 'arrow-left',
      variant: 'secondary',
      label: backLabel,
      children: <Link href={backHref || '/'} />
    }} data-sentry-element="InteractionHeader" data-sentry-source-file="ManifestOverviewBlock.tsx" />
    </div>;
};
export const ManifestOverviewBlock: ComponentBlock<ManifestOverviewBlockProps> = ({
  data,
  fallback
}) => {
  const router = useRouter();
  const isAutoRedirectingSingle = data?.travellers?.length === 1 && !!data.travellers[0].href;
  useLayoutEffect(() => {
    if (isAutoRedirectingSingle && data?.travellers?.[0]?.href) {
      router.replace(data.travellers[0].href);
    }
  }, [isAutoRedirectingSingle, data?.travellers, router]);
  const isPinRequired = (travellerId: string): boolean => {
    const traveller = data?.travellers?.find(t => t.id === travellerId);
    if (!traveller?.pin || !data || data.travellers?.length === 1) return false;
    const persistedPin = getPersistedPin(traveller.mpi);
    return traveller.pin !== persistedPin;
  };
  const handlePinEnter = (travellerId: string, enteredPin: string): boolean => {
    const traveller = data?.travellers?.find(t => t.id === travellerId);
    if (traveller?.pin !== enteredPin) return false;
    setPersistedPin(traveller.mpi, enteredPin);
    return true;
  };
  return <>
      {!isAutoRedirectingSingle && <ManifestOverviewHeader title={data?.i18n?.headerTitle} backLabel={data?.i18n?.headerBack} />}
      <ManifestOverview {...data} fallback={fallback ?? data?.fallback} isPinRequired={isPinRequired} onPinEnter={handlePinEnter} data-sentry-element="ManifestOverview" data-sentry-source-file="ManifestOverviewBlock.tsx" />
    </>;
};